import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

/**
 * Header user dropdown shared by AuthedLayout + EmployeeLayout. Replaces the
 * old standalone "Sign out" button and the sidebar's Security link: account
 * management (password reset, sessions) is per-user, not tenant config, so it
 * lives here rather than in the left nav. Items: Account & security (→
 * /security, which serves both audiences) and Sign out.
 */
export function UserMenu({
  user,
  onLogout,
}: {
  user: { email: string; role: string };
  onLogout: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"
        aria-haspopup="menu"
        aria-expanded={open}
        title={user.email}
      >
        <span>
          {user.email} · {user.role}
        </span>
        <span className="text-xs">{open ? '▴' : '▾'}</span>
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 mt-2 w-56 rounded border border-gray-800 bg-black shadow-lg z-50">
          {/* Billing & plan — tenant subscription management. Admin-level, so
              hidden for the employee self-service role (they'd be bounced by
              the /billing route guard anyway). ADR-0026 PR-3b-3 follow-up. */}
          {user.role !== 'employee' ? (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  void navigate({ to: '/billing' });
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white"
              >
                Billing &amp; plan
              </button>
              <div className="border-t border-gray-800" />
            </>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void navigate({ to: '/security' });
            }}
            className="block w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white"
          >
            Account &amp; security
          </button>
          <div className="border-t border-gray-800" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="block w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Sidebar nav item + group shapes, shared by both layouts. */
export interface NavItem {
  label: string;
  to: string;
  disabled?: boolean;
  tag?: string;
}
export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * The single nav item that best matches `pathname`: the one whose `to` is the
 * longest exact-or-slash-bounded prefix. Longest-match so a child route
 * (/loans/requests, or /me/payslips) highlights itself and not its parent
 * (/loans, or the /me Home item) — the parent's prefix also matches, but the
 * child's is longer and wins. Returns the matching `to`, or null.
 */
export function activeNavTo(pathname: string, groups: NavGroup[]): string | null {
  let best: string | null = null;
  for (const group of groups) {
    for (const { to } of group.items) {
      if ((pathname === to || pathname.startsWith(`${to}/`)) && (best === null || to.length > best.length)) {
        best = to;
      }
    }
  }
  return best;
}
